/**
 * Amazon Reviews — Judge.me V2 Style
 * Web component that fetches review data from a JSON asset file
 * and renders a vertical list of reviews with pagination.
 *
 * Layout per review:
 *  1. Stars (green, 24px)
 *  2. Avatar + Name + Verified badge (same line)
 *  3. Title (bold)
 *  4. Body (with read more)
 *  5. Date
 *
 * Pagination: pill-shaped buttons, green active state
 */

if (!customElements.get('amazon-reviews-slider')) {
  customElements.define(
    'amazon-reviews-slider',
    class AmazonReviewsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.amazon-reviews__track');
        this.reviews = [];
        this.currentPage = 1;
        this.perPage = 5;

        if (!this.track) return;

        this._onReadMoreClick = this._onReadMoreClick.bind(this);
        this._onPaginationClick = this._onPaginationClick.bind(this);
        this._onImageClick = this._onImageClick.bind(this);
        this._onKeyDown = this._onKeyDown.bind(this);

        this.addEventListener('click', this._onReadMoreClick);
        this.addEventListener('click', this._onPaginationClick);
        this.addEventListener('click', this._onImageClick);

        this._lightboxImages = [];
        this._lightboxIndex = 0;

        this._fetchAndRender();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onReadMoreClick);
        this.removeEventListener('click', this._onPaginationClick);
        this.removeEventListener('click', this._onImageClick);
        this._closeLightbox();
      }

      /* ---- Data Fetching & Rendering ---- */

      async _fetchAndRender() {
        var url = this.getAttribute('data-reviews-url');
        if (!url) return;

        try {
          var response = await fetch(url);
          if (!response.ok) throw new Error('Failed to load reviews');
          var data = await response.json();

          this.reviews = data.reviews || [];
          this._renderPage();
        } catch (err) {
          console.error('Amazon Reviews: Could not load review data.', err);
        }
      }

      _renderPage() {
        if (!this.track || !this.reviews.length) return;

        var totalPages = Math.ceil(this.reviews.length / this.perPage);
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        if (this.currentPage < 1) this.currentPage = 1;

        var start = (this.currentPage - 1) * this.perPage;
        var end = start + this.perPage;
        var pageReviews = this.reviews.slice(start, end);

        var html = '';
        for (var i = 0; i < pageReviews.length; i++) {
          html += this._buildCardHTML(pageReviews[i]);
        }

        this.track.innerHTML = html;
        this._renderPagination(totalPages);

        if (this._hasNavigated) {
          var section = this.closest('.amazon-reviews');
          if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }

      _buildCardHTML(review) {
        var starsHTML = this._buildStarsHTML(review.rating, 24);
        var body = review.body || '';
        var truncateLength = 200;
        var needsTruncation = body.length > truncateLength;

        var fullBody = this._escapeHTML(body).replace(/\n/g, '<br>');

        var truncatedBody = '';
        if (needsTruncation) {
          var truncated = body.substring(0, truncateLength);
          var lastSpace = truncated.lastIndexOf(' ');
          if (lastSpace > 120) truncated = truncated.substring(0, lastSpace);
          truncatedBody = this._escapeHTML(truncated) + '...';
        }

        var dateStr = this._formatDate(review.date);
        var initial = (review.name && review.name.charAt(0)) || '?';
        var avatarColor = this._getAvatarColor(review.name);

        return (
          '<div class="amazon-reviews__card">' +
            /* Row 1: Stars */
            '<div class="amazon-reviews__card-stars" aria-label="' + review.rating + ' out of 5 stars">' +
              starsHTML +
            '</div>' +
            /* Row 2: Avatar + Name + Verified */
            '<div class="amazon-reviews__card-author-row">' +
              '<div class="amazon-reviews__card-avatar amazon-reviews__card-avatar--initials" style="background:' + avatarColor + '">' +
                this._escapeHTML(initial) +
              '</div>' +
              '<span class="amazon-reviews__card-name">' + this._escapeHTML(review.name) + '</span>' +
              '<span class="amazon-reviews__card-verified">Verified</span>' +
            '</div>' +
            /* Row 3: Title */
            '<h3 class="amazon-reviews__card-title">' + this._escapeHTML(review.title) + '</h3>' +
            /* Row 4: Body */
            '<div class="amazon-reviews__card-body">' +
              (needsTruncation
                ? '<div class="amazon-reviews__card-body-truncated">' + truncatedBody + '</div>' +
                  '<div class="amazon-reviews__card-body-full" style="display:none">' + fullBody + '</div>' +
                  '<button class="amazon-reviews__read-more">Read more</button>'
                : '<div class="amazon-reviews__card-body-truncated">' + fullBody + '</div>'
              ) +
            '</div>' +
            /* Row 5: Images (if any) */
            this._buildImagesHTML(review.images) +
            /* Row 6: Date */
            '<div class="amazon-reviews__card-footer">' +
              '<span class="amazon-reviews__card-date">' + dateStr + '</span>' +
            '</div>' +
          '</div>'
        );
      }

      _renderPagination(totalPages) {
        if (totalPages <= 1) return;

        var existing = this.querySelector('.amazon-reviews__pagination');
        if (existing) existing.remove();

        var html = '<div class="amazon-reviews__pagination">';

        /* Prev arrow */
        html += '<button class="amazon-reviews__page-btn amazon-reviews__page-btn--arrow" data-page="prev"' +
          (this.currentPage <= 1 ? ' disabled' : '') + '>' +
          '<svg width="7" height="12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.45389 5.99976L6.52714 10.0728C6.66548 10.2113 6.73631 10.3853 6.73964 10.595C6.74281 10.8045 6.67198 10.9818 6.52714 11.1268C6.38214 11.2716 6.20648 11.344 6.00014 11.344C5.79381 11.344 5.61814 11.2716 5.47314 11.1268L0.978894 6.63251C0.885394 6.53884 0.819394 6.44009 0.780894 6.33626C0.742394 6.23243 0.723145 6.12026 0.723145 5.99976C0.723145 5.87926 0.742394 5.76709 0.780894 5.66326C0.819394 5.55943 0.885394 5.46068 0.978894 5.36701L5.47314 0.872758C5.61164 0.734425 5.78573 0.663591 5.99539 0.660258C6.20489 0.657091 6.38214 0.727925 6.52714 0.872758C6.67198 1.01776 6.74439 1.19342 6.74439 1.39976C6.74439 1.60609 6.67198 1.78176 6.52714 1.92676L2.45389 5.99976Z" fill="currentColor"/></svg>' +
          '</button>';

        /* Page numbers */
        var pages = this._getPageNumbers(this.currentPage, totalPages);
        for (var i = 0; i < pages.length; i++) {
          if (pages[i] === '...') {
            html += '<span class="amazon-reviews__page-ellipsis">...</span>';
          } else {
            var isActive = pages[i] === this.currentPage;
            html += '<button class="amazon-reviews__page-btn' +
              (isActive ? ' amazon-reviews__page-btn--active' : '') +
              '" data-page="' + pages[i] + '">' + pages[i] + '</button>';
          }
        }

        /* Next arrow */
        html += '<button class="amazon-reviews__page-btn amazon-reviews__page-btn--arrow" data-page="next"' +
          (this.currentPage >= totalPages ? ' disabled' : '') + '>' +
          '<svg width="7" height="12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.94626 6.00001L0.873012 1.92701C0.734678 1.78851 0.663845 1.61443 0.660512 1.40476C0.657345 1.19526 0.728178 1.01801 0.873012 0.873012C1.01801 0.728178 1.19368 0.655762 1.40001 0.655762C1.60634 0.655762 1.78201 0.728178 1.92701 0.873012L6.42126 5.36726C6.51476 5.46093 6.58076 5.55968 6.61926 5.66351C6.65776 5.76735 6.67701 5.87951 6.67701 6.00001C6.67701 6.12051 6.65776 6.23268 6.61926 6.33651C6.58076 6.44035 6.51476 6.5391 6.42126 6.63276L1.92701 11.127C1.78851 11.2653 1.61443 11.3362 1.40476 11.3395C1.19526 11.3427 1.01801 11.2718 0.873012 11.127C0.728178 10.982 0.655762 10.8063 0.655762 10.6C0.655762 10.3937 0.728178 10.218 0.873012 10.073L4.94626 6.00001Z" fill="currentColor"/></svg>' +
          '</button>';

        html += '</div>';

        var temp = document.createElement('div');
        temp.innerHTML = html;
        this.appendChild(temp.firstElementChild);
      }

      _getPageNumbers(current, total) {
        if (total <= 7) {
          var arr = [];
          for (var i = 1; i <= total; i++) arr.push(i);
          return arr;
        }

        var pages = [1];
        if (current > 3) pages.push('...');

        var start = Math.max(2, current - 1);
        var end = Math.min(total - 1, current + 1);

        for (var j = start; j <= end; j++) {
          pages.push(j);
        }

        if (current < total - 2) pages.push('...');
        pages.push(total);
        return pages;
      }

      /* ---- Stars ---- */

      _buildStarsHTML(rating, size) {
        var html = '';
        var full = Math.floor(rating);
        var hasHalf = rating - full >= 0.25 && rating - full < 0.75;
        var empty = 5 - full - (hasHalf ? 1 : 0);

        if (rating - full >= 0.75) {
          full += 1;
          empty = 5 - full;
          hasHalf = false;
        }

        /* Judge.me V2 star path */
        var starPath = 'M5.79584 1.27976C6.02014 0.793465 6.71129 0.793466 6.9356 1.27976L8.34335 4.33176C8.43476 4.52996 8.62259 4.66642 8.83933 4.69212L12.177 5.08785C12.7088 5.1509 12.9224 5.80822 12.5292 6.17182L10.0616 8.45379C9.90133 8.60198 9.82959 8.82279 9.87213 9.03686L10.5272 12.3334C10.6315 12.8587 10.0724 13.2649 9.60507 13.0034L6.67225 11.3617C6.4818 11.2551 6.24963 11.2551 6.05918 11.3617L3.12636 13.0034C2.65906 13.265 2.09991 12.8587 2.20428 12.3334L2.8593 9.03686C2.90184 8.82279 2.8301 8.60198 2.66985 8.45379L0.202248 6.17182C-0.190933 5.80822 0.022645 5.1509 0.554454 5.08785L3.8921 4.69212C4.10884 4.66642 4.29667 4.52995 4.38808 4.33176L5.79584 1.27976Z';

        for (var i = 0; i < full; i++) {
          html += '<svg class="amazon-reviews__star amazon-reviews__star--filled" viewBox="0 0 13 14" width="' + size + '" height="' + size + '" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="' + starPath + '"/></svg>';
        }

        if (hasHalf) {
          var halfId = 'half-' + Math.random().toString(36).substr(2, 6);
          html +=
            '<svg class="amazon-reviews__star" viewBox="0 0 13 14" width="' + size + '" height="' + size + '" fill="none" xmlns="http://www.w3.org/2000/svg">' +
              '<defs><clipPath id="' + halfId + '"><rect x="0" y="0" width="6.5" height="14"/></clipPath></defs>' +
              '<path d="' + starPath + '" fill="#ddd"/>' +
              '<path d="' + starPath + '" fill="#0CA719" clip-path="url(#' + halfId + ')"/>' +
            '</svg>';
        }

        for (var j = 0; j < empty; j++) {
          html += '<svg class="amazon-reviews__star amazon-reviews__star--empty" viewBox="0 0 13 14" width="' + size + '" height="' + size + '" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="' + starPath + '"/></svg>';
        }

        return html;
      }

      /* ---- Utilities ---- */

      _formatDate(dateStr) {
        if (!dateStr) return '';
        var parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;

        var months = [
          'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];
        var monthIndex = parseInt(parts[1], 10) - 1;
        if (isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return dateStr;
        var day = parseInt(parts[2], 10);
        var year = parts[0];

        return months[monthIndex] + ' ' + day + ', ' + year;
      }

      _escapeHTML(str) {
        if (!str) return '';
        return str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      _getAvatarColor(name) {
        if (!name) return '#999';
        var colors = [
          '#e57373', '#f06292', '#ba68c8', '#9575cd',
          '#7986cb', '#64b5f6', '#4fc3f7', '#4dd0e1',
          '#4db6ac', '#81c784', '#aed581', '#dce775',
          '#ffd54f', '#ffb74d', '#ff8a65', '#a1887f'
        ];
        var hash = 0;
        for (var i = 0; i < name.length; i++) {
          hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
      }

      /* ---- Images ---- */

      _buildImagesHTML(images) {
        if (!images || !images.length) return '';

        var html = '<div class="amazon-reviews__card-images">';
        for (var i = 0; i < images.length; i++) {
          html +=
            '<button class="amazon-reviews__card-image-link" type="button" data-lightbox-src="' + this._escapeHTML(images[i]) + '" data-lightbox-index="' + i + '" data-lightbox-total="' + images.length + '">' +
              '<img class="amazon-reviews__card-image" src="' + this._escapeHTML(images[i]) + '" alt="Review image ' + (i + 1) + '" loading="lazy">' +
            '</button>';
        }
        html += '</div>';
        return html;
      }

      /* ---- Event Handlers ---- */

      _onReadMoreClick(e) {
        var btn = e.target.closest('.amazon-reviews__read-more');
        if (!btn) return;

        var body = btn.closest('.amazon-reviews__card-body');
        if (!body) return;

        var truncated = body.querySelector('.amazon-reviews__card-body-truncated');
        var full = body.querySelector('.amazon-reviews__card-body-full');
        var isExpanded = body.classList.contains('is-expanded');

        if (isExpanded) {
          body.classList.remove('is-expanded');
          btn.textContent = 'Read more';
          if (truncated) truncated.style.display = '';
          if (full) full.style.display = 'none';
        } else {
          body.classList.add('is-expanded');
          btn.textContent = 'Read less';
          if (truncated) truncated.style.display = 'none';
          if (full) full.style.display = '';
        }
      }

      _onPaginationClick(e) {
        var btn = e.target.closest('.amazon-reviews__page-btn');
        if (!btn || btn.disabled) return;

        var page = btn.getAttribute('data-page');
        if (!page) return;

        var totalPages = Math.ceil(this.reviews.length / this.perPage);

        if (page === 'prev') {
          if (this.currentPage > 1) this.currentPage -= 1;
        } else if (page === 'next') {
          if (this.currentPage < totalPages) this.currentPage += 1;
        } else {
          this.currentPage = parseInt(page, 10);
        }

        this._hasNavigated = true;
        this._renderPage();
      }

      /* ---- Lightbox ---- */

      _onImageClick(e) {
        var btn = e.target.closest('[data-lightbox-src]');
        if (!btn) return;

        e.preventDefault();

        /* Collect all images from the same review card */
        var card = btn.closest('.amazon-reviews__card');
        if (!card) return;

        var buttons = card.querySelectorAll('[data-lightbox-src]');
        this._lightboxImages = [];
        for (var i = 0; i < buttons.length; i++) {
          this._lightboxImages.push(buttons[i].getAttribute('data-lightbox-src'));
        }

        this._lightboxIndex = parseInt(btn.getAttribute('data-lightbox-index'), 10) || 0;
        this._openLightbox();
      }

      _openLightbox() {
        if (this._lightbox) this._closeLightbox();

        var overlay = document.createElement('div');
        overlay.className = 'amazon-reviews-lightbox';

        var hasMultiple = this._lightboxImages.length > 1;

        overlay.innerHTML =
          '<div class="amazon-reviews-lightbox__backdrop"></div>' +
          '<button class="amazon-reviews-lightbox__close" type="button" aria-label="Close">' +
            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
              '<path d="M15 5L5 15M5 5l10 10" stroke="#fff" stroke-width="2" stroke-linecap="round"/>' +
            '</svg>' +
          '</button>' +
          (hasMultiple
            ? '<button class="amazon-reviews-lightbox__nav amazon-reviews-lightbox__nav--prev" type="button" aria-label="Previous image">' +
                '<svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                  '<path d="M10 2L2 10l8 8" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</button>'
            : '') +
          '<div class="amazon-reviews-lightbox__img-wrap">' +
            '<img class="amazon-reviews-lightbox__img" src="' + this._escapeHTML(this._lightboxImages[this._lightboxIndex]) + '" alt="Review image">' +
          '</div>' +
          (hasMultiple
            ? '<button class="amazon-reviews-lightbox__nav amazon-reviews-lightbox__nav--next" type="button" aria-label="Next image">' +
                '<svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                  '<path d="M2 2l8 8-8 8" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg>' +
              '</button>'
            : '') +
          (hasMultiple
            ? '<div class="amazon-reviews-lightbox__counter">' + (this._lightboxIndex + 1) + ' / ' + this._lightboxImages.length + '</div>'
            : '');

        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';
        this._lightbox = overlay;

        /* Bind lightbox events */
        var self = this;

        overlay.querySelector('.amazon-reviews-lightbox__backdrop').addEventListener('click', function() {
          self._closeLightbox();
        });

        overlay.querySelector('.amazon-reviews-lightbox__close').addEventListener('click', function() {
          self._closeLightbox();
        });

        var prevBtn = overlay.querySelector('.amazon-reviews-lightbox__nav--prev');
        var nextBtn = overlay.querySelector('.amazon-reviews-lightbox__nav--next');

        if (prevBtn) {
          prevBtn.addEventListener('click', function() {
            self._lightboxIndex = (self._lightboxIndex - 1 + self._lightboxImages.length) % self._lightboxImages.length;
            self._updateLightboxImage();
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', function() {
            self._lightboxIndex = (self._lightboxIndex + 1) % self._lightboxImages.length;
            self._updateLightboxImage();
          });
        }

        document.addEventListener('keydown', this._onKeyDown);
      }

      _updateLightboxImage() {
        if (!this._lightbox) return;

        var img = this._lightbox.querySelector('.amazon-reviews-lightbox__img');
        if (img) img.src = this._lightboxImages[this._lightboxIndex];

        var counter = this._lightbox.querySelector('.amazon-reviews-lightbox__counter');
        if (counter) counter.textContent = (this._lightboxIndex + 1) + ' / ' + this._lightboxImages.length;
      }

      _closeLightbox() {
        if (this._lightbox) {
          this._lightbox.remove();
          this._lightbox = null;
          document.body.style.overflow = '';
        }
        document.removeEventListener('keydown', this._onKeyDown);
      }

      _onKeyDown(e) {
        if (!this._lightbox) return;

        if (e.key === 'Escape') {
          this._closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          this._lightboxIndex = (this._lightboxIndex - 1 + this._lightboxImages.length) % this._lightboxImages.length;
          this._updateLightboxImage();
        } else if (e.key === 'ArrowRight') {
          this._lightboxIndex = (this._lightboxIndex + 1) % this._lightboxImages.length;
          this._updateLightboxImage();
        }
      }
    }
  );
}
